/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TableColumnsType, Typography } from 'antd';
import { formatDateTime } from '@/utils/common';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { Table } from '@/components/tables/table';
import {
  getListActivityLogsUserAdmin,
  watchAdminUserLoading,
  watchAdminUserPagination,
  watchListActivityLogsUserAdmin
} from '@/redux/slices/admin.slice';
import { AdminActivityLogSearcher } from '@/components/forms/admin-activity-log-searcher.form';
import dayjs from 'dayjs';
import { tableScrollY } from '@/utils/common';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  filter?: FilterAdminActivityLog;
};

export default function AdminActivityLog() {
  const dispatch = useAppDispatch();

  const listActivityLogsUserClient = useAppSelector(
    watchListActivityLogsUserAdmin
  );
  const pagination = useAppSelector(watchAdminUserPagination);
  const loading = useAppSelector(watchAdminUserLoading);

  const [filter, setFilter] = useState<FilterAdminActivityLog>({});

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      filter
    }: PageChangeParams) => {
      dispatch(
        getListActivityLogsUserAdmin({
          offset: page,
          limit: pageSize,
          search: filter?.textSearch,
          userId: filter?.userId,
          moduleIndex: filter?.function,
          fromDate:
            filter?.timeRange &&
            dayjs(filter?.timeRange[0]).format('YYYY-MM-DD'),
          toDate:
            filter?.timeRange &&
            dayjs(filter?.timeRange[1]).format('YYYY-MM-DD')
        })
      );
    },
    [dispatch]
  );

  const handleSearch = (values: FilterAdminActivityLog) => {
    const newFilter: FilterAdminActivityLog = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ filter: newFilter });
  };

  const handleReset = () => {
    handlePageChange({ filter });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  const columns: TableColumnsType<ActivityLog> = [
    {
      title: 'Thời gian hoạt động',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 200,
      render: (record) => formatDateTime(record)
    },
    {
      title: 'Người quản trị',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200
    },
    {
      title: 'Chức năng',
      dataIndex: 'module',
      key: 'module'
    },
    {
      title: 'Hoạt động',
      dataIndex: 'action',
      key: 'action'
    },

    {
      title: 'Mô tả chi tiết',
      dataIndex: 'desc',
      key: 'desc'
    }
  ];

  return (
    <div css={rootStyles}>
      <Typography.Title level={5}>
        Ghi lại hoạt động admin (Admin Activity Log)
      </Typography.Title>
      <AdminActivityLogSearcher onSearch={handleSearch} />
      <Table<ActivityLog>
        rowKey={(record) => record.createdDate}
        columns={columns}
        dataSource={listActivityLogsUserClient}
        scrollY={tableScrollY(pagination.limitPage)}
        placeholder='Nhập từ khoá để tìm kiếm theo mô tả chi tiết'
        loading={loading}
        pagination={{
          showQuickJumper: true,
          current: pagination.curPage,
          pageSize: pagination.limitPage,
          total: pagination.totalRows,
          onChange: (page, pageSize) => {
            handlePageChange({ page, pageSize, filter });
          }
        }}
        totalResult={pagination.totalRows}
        onFilter={handleSearch}
        onReset={handleReset}
      />
    </div>
  );
}

const rootStyles = css`
  padding: 2rem 2.4rem;
`;
